import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Image as ImageIcon, CheckCircle2, X, Star } from 'lucide-react';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import imageCompression from 'browser-image-compression';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEIGHBOURHOODS = [
  // Atlantic Seaboard
  'Sea Point', 'Green Point', 'Mouille Point', 'De Waterkant', 'V&A Waterfront',
  'Camps Bay', 'Clifton', 'Bantry Bay', 'Bakoven', 'Fresnaye', 'Llandudno', 'Hout Bay',
  // City Bowl
  'Gardens', 'Tamboerskloof', 'Vredehoek', 'Oranjezicht', 'Higgovale',
  'City Bowl', 'Devil\'s Peak Estate', 'Zonnebloem', 'Bo-Kaap',
  // Southern Suburbs
  'Woodstock', 'Observatory', 'Salt River', 'Rosebank', 'Rondebosch',
  'Newlands', 'Claremont', 'Kenilworth', 'Bishopscourt', 'Wynberg',
  'Constantia', 'Tokai', 'Bergvliet', 'Plumstead', 'Diep River', 'Southfield',
  // South Peninsula
  'Muizenberg', 'Kalk Bay', 'Fish Hoek', 'Simon\'s Town', 'Noordhoek',
  // Northern Suburbs
  'Century City', 'Milnerton', 'Table View', 'Bloubergstrand', 'Parow', 'Bellville',
  // Winelands / East
  'Somerset West', 'Stellenbosch', 'Durbanville'
];

const PROPERTY_TYPES = ['Apartment', 'Studio', 'House', 'Villa', 'Penthouse', 'Cottage', 'Loft', 'Coliving', 'Guesthouse'];

const AMENITIES_GROUPS = [
  {
    title: 'Essentials',
    items: [
      { key: 'workspace', label: 'Workspace / Desk' },
      { key: 'kitchen', label: 'Full kitchen' },
      { key: 'laundry', label: 'Laundry (washer)' },
      { key: 'air_conditioning', label: 'Air conditioning' },
      { key: 'heating', label: 'Heating' },
      { key: 'utilities_included', label: 'Utilities included' },
      { key: 'internet_included', label: 'Internet included' },
    ]
  },
  {
    title: 'Kitchen',
    items: [
      { key: 'refrigerator', label: 'Refrigerator' },
      { key: 'microwave', label: 'Microwave' },
      { key: 'dishwasher', label: 'Dishwasher' },
      { key: 'oven', label: 'Oven' },
      { key: 'stove', label: 'Stove' },
      { key: 'coffee_maker', label: 'Coffee maker' },
    ]
  },
  {
    title: 'Bathroom & bedroom',
    items: [
      { key: 'hair_dryer', label: 'Hair dryer' },
      { key: 'iron', label: 'Iron' },
      { key: 'bed_linens', label: 'Bed linens provided' },
      { key: 'extra_pillows', label: 'Extra pillows & blankets' },
    ]
  },
  {
    title: 'Entertainment',
    items: [
      { key: 'tv', label: 'TV' },
      { key: 'smart_tv', label: 'Smart TV' },
      { key: 'cable_tv', label: 'Cable TV' },
    ]
  },
  {
    title: 'Outdoor & views',
    items: [
      { key: 'balcony', label: 'Balcony' },
      { key: 'outdoor_space', label: 'Outdoor space / Garden' },
      { key: 'bbq', label: 'BBQ / Braai' },
      { key: 'pool', label: 'Pool' },
      { key: 'hot_tub', label: 'Hot tub' },
      { key: 'sea_view', label: 'Sea view' },
      { key: 'mountain_view', label: 'Mountain view' },
      { key: 'city_view', label: 'City view' },
      { key: 'garden_view', label: 'Garden view' },
      { key: 'beachfront', label: 'Beachfront' },
      { key: 'waterfront', label: 'Waterfront' },
    ]
  },
  {
    title: 'Parking & access',
    items: [
      { key: 'parking', label: 'Parking' },
      { key: 'ev_charger', label: 'EV charger' },
      { key: 'self_check_in', label: 'Self check-in' },
      { key: 'private_entrance', label: 'Private entrance' },
    ]
  },
  {
    title: 'Safety',
    items: [
      { key: 'security', label: 'Security / Alarm' },
      { key: 'smoke_alarm', label: 'Smoke alarm' },
      { key: 'carbon_monoxide_alarm', label: 'CO alarm' },
      { key: 'fire_extinguisher', label: 'Fire extinguisher' },
      { key: 'first_aid_kit', label: 'First aid kit' },
    ]
  },
  {
    title: 'Extras',
    items: [
      { key: 'gym', label: 'Gym' },
      { key: 'pet_friendly', label: 'Pet friendly' },
      { key: 'smoking', label: 'Smoking allowed' },
      { key: 'crib', label: 'Crib' },
      { key: 'high_chair', label: 'High chair' },
    ]
  },
];

const MONTHS = (() => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en', { month: 'long', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
})();

export const CreateListing = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get('edit'); // optional listing id to edit
  const [step, setStep] = useState(1);
  const [checking, setChecking] = useState(true);
  const [canCreate, setCanCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listingId, setListingId] = useState(editingId || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [heroPhotoId, setHeroPhotoId] = useState(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    property_type: 'Apartment',
    place_type: 'entire_place',
    bedrooms: 1,
    bathrooms: 1,
    square_metres: '',
    neighbourhood: '',
    approximate_address: '',
    lat: -33.925,
    lng: 18.410,
    monthly_price: '',
    monthly_prices: {},
    available_months: [],
    minimum_stay: 28,
    maximum_stay: '',
    wifi_speed: '',
    // Essentials
    workspace: false, parking: false, pool: false, gym: false, laundry: false,
    kitchen: true, pet_friendly: false, air_conditioning: false, heating: false,
    smoking: false, utilities_included: false, internet_included: false,
    security: false, balcony: false,
    // Kitchen
    refrigerator: true, microwave: false, dishwasher: false, oven: false,
    stove: false, coffee_maker: false,
    // Bathroom & bedroom
    hair_dryer: false, iron: false, bed_linens: true, extra_pillows: false,
    // Entertainment
    tv: false, cable_tv: false, smart_tv: false,
    // Safety
    smoke_alarm: false, fire_extinguisher: false, first_aid_kit: false,
    carbon_monoxide_alarm: false,
    // Access
    self_check_in: false, private_entrance: false,
    // Views
    sea_view: false, mountain_view: false, garden_view: false, city_view: false,
    // Outdoor
    beachfront: false, waterfront: false, outdoor_space: false, bbq: false,
    hot_tub: false, ev_charger: false,
    // Family
    crib: false, high_chair: false,
    // Contact
    contact_phone: '',
    contact_email: user?.email || '',
    contact_whatsapp: '',
    contact_phone_visible: false,  // Default: don't show phone
    contact_email_visible: true,
    contact_whatsapp_visible: true,
    promo_consent: false,
  });

  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  useEffect(() => {
    if (!authLoading && user) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const init = async () => {
    if (editingId) {
      // Load existing listing
      try {
        const { data } = await axios.get(`${API}/listings/${editingId}`);
        setForm((f) => ({
          ...f,
          ...data,
          monthly_prices: data.monthly_prices || {},
          square_metres: data.square_metres ?? '',
          wifi_speed: data.wifi_speed ?? '',
          maximum_stay: data.maximum_stay ?? '',
        }));
        setUploadedPhotos(data.photos || []);
        setCanCreate(true);
      } catch (e) {
        toast.error('Failed to load listing');
      } finally {
        setChecking(false);
      }
      return;
    }
    try {
      const { data } = await axios.get(`${API}/host/dashboard`, { withCredentials: true });
      setCanCreate(data.can_create_listing);
    } catch (e) {
      setCanCreate(false);
    } finally {
      setChecking(false);
    }
  };

  const updateField = (key, value) => setForm({ ...form, [key]: value });

  const validateStep = (s) => {
    if (s === 1) {
      if (!form.title || !form.description || !form.property_type) {
        toast.error('Please fill in title, description, and property type');
        return false;
      }
    }
    if (s === 2) {
      if (!form.neighbourhood || !form.approximate_address) {
        toast.error('Please select neighbourhood and enter approximate address');
        return false;
      }
    }
    if (s === 3) {
      if (!form.monthly_price || form.monthly_price < 1000) {
        toast.error('Please enter a valid base monthly price');
        return false;
      }
      if (form.available_months.length === 0) {
        toast.error('Please select at least one available month');
        return false;
      }
      const missing = form.available_months.filter((m) => !form.monthly_prices?.[m] || form.monthly_prices[m] < 1000);
      if (missing.length > 0) {
        toast.error(`Set a monthly price for each available month`);
        return false;
      }
      if (parseInt(form.minimum_stay) < 28) {
        toast.error('Minimum stay must be at least 28 nights');
        return false;
      }
    }
    if (s === 4) {
      if (!form.contact_whatsapp || form.contact_whatsapp.replace(/\D/g, '').length < 9) {
        toast.error('Please enter your WhatsApp number with country code');
        return false;
      }
    }
    return true;
  };

  const handleSubmitListing = async () => {
    if (!validateStep(4)) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bedrooms: parseInt(form.bedrooms),
        bathrooms: parseInt(form.bathrooms),
        square_metres: form.square_metres ? parseInt(form.square_metres) : null,
        wifi_speed: form.wifi_speed ? parseInt(form.wifi_speed) : null,
        minimum_stay: parseInt(form.minimum_stay),
        maximum_stay: form.maximum_stay ? parseInt(form.maximum_stay) : null,
        monthly_price: parseFloat(form.monthly_price),
        monthly_prices: Object.fromEntries(
          Object.entries(form.monthly_prices || {}).map(([k, v]) => [k, parseFloat(v)])
        ),
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
      };
      if (editingId) {
        await axios.patch(`${API}/listings/${editingId}`, payload, { withCredentials: true });
        toast.success('Listing updated.');
        setStep(5);
      } else {
        const { data } = await axios.post(`${API}/listings`, payload, { withCredentials: true });
        setListingId(data.id || data._id);
        toast.success('Property saved! Now upload your photos.');
        setStep(5);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    } finally {
      setSubmitting(false);
    }
  };

  // Save draft: persists whatever the host has typed in the current step
  // without moving them forward. Works for both new (POST) and editing (PATCH).
  const [savingDraft, setSavingDraft] = useState(false);
  const handleSaveDraft = async () => {
    if (savingDraft) return;
    if (!form.contact_whatsapp || form.contact_whatsapp.replace(/\D/g, '').length < 9) {
      toast.error('Add your WhatsApp number (step 4) before saving a draft');
      return;
    }
    setSavingDraft(true);
    try {
      const draft = {
        ...form,
        bedrooms: parseInt(form.bedrooms) || 1,
        bathrooms: parseInt(form.bathrooms) || 1,
        square_metres: form.square_metres ? parseInt(form.square_metres) : null,
        wifi_speed: form.wifi_speed ? parseInt(form.wifi_speed) : null,
        minimum_stay: parseInt(form.minimum_stay) || 28,
        maximum_stay: form.maximum_stay ? parseInt(form.maximum_stay) : null,
        monthly_price: form.monthly_price ? parseFloat(form.monthly_price) : 0,
        monthly_prices: Object.fromEntries(
          Object.entries(form.monthly_prices || {}).map(([k, v]) => [k, parseFloat(v) || 0])
        ),
        lat: parseFloat(form.lat) || -33.925,
        lng: parseFloat(form.lng) || 18.410,
      };
      if (listingId) {
        await axios.patch(`${API}/listings/${listingId}`, draft, { withCredentials: true });
      } else {
        // On first draft save, we need enough required fields — fall back to placeholders
        // so the PATCH endpoint on later saves can update anything.
        const seedTitle = draft.title || 'Untitled draft';
        const seedDesc = draft.description || 'Draft in progress';
        const seedNeigh = draft.neighbourhood || 'Sea Point';
        const seedAddress = draft.approximate_address || 'Cape Town, South Africa';
        const seedPrice = draft.monthly_price || 15000;
        const seedMonths = draft.available_months && draft.available_months.length
          ? draft.available_months
          : [MONTHS[0].value];
        const seedPrices = Object.keys(draft.monthly_prices).length
          ? draft.monthly_prices
          : Object.fromEntries(seedMonths.map((m) => [m, seedPrice]));
        const seed = {
          ...draft,
          title: seedTitle,
          description: seedDesc,
          neighbourhood: seedNeigh,
          approximate_address: seedAddress,
          monthly_price: seedPrice,
          available_months: seedMonths,
          monthly_prices: seedPrices,
        };
        const { data } = await axios.post(`${API}/listings`, seed, { withCredentials: true });
        setListingId(data.id || data._id);
      }
      toast.success('Draft saved. Your progress is safe.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (uploadedPhotos.length + files.length > 8) {
      toast.error('Maximum 8 photos allowed');
      return;
    }
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is over 10MB — please compress it and try again`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return;
      }
    }
    setUploadingPhoto(true);
    // Compress in browser BEFORE upload so slow connections still succeed on 10MB originals.
    // Target ~1.5MB, longest side 2400px, preserving orientation.
    const compressionOptions = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 2400,
      useWebWorker: true,
      initialQuality: 0.82,
      fileType: 'image/jpeg',
    };
    for (const rawFile of files) {
      try {
        let toUpload = rawFile;
        // Only compress if it's actually worth it (>500KB or non-jpeg).
        if (rawFile.size > 500 * 1024 || rawFile.type !== 'image/jpeg') {
          try {
            const compressed = await imageCompression(rawFile, compressionOptions);
            // Give the compressed blob a proper filename with .jpg
            const baseName = rawFile.name.replace(/\.[^.]+$/, '') || 'photo';
            toUpload = new File([compressed], `${baseName}.jpg`, { type: 'image/jpeg' });
          } catch (compressErr) {
            // Fall back to raw file if compression fails.
            console.warn('Image compression failed, uploading original', compressErr);
            toUpload = rawFile;
          }
        }
        const formData = new FormData();
        formData.append('file', toUpload);
        const { data } = await axios.post(
          `${API}/listings/${listingId}/upload-photo`,
          formData,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setUploadedPhotos((prev) => [...prev, data]);
      } catch (error) {
        toast.error(`Failed to upload ${rawFile.name}: ${error.response?.data?.detail || ''}`);
      }
    }
    setUploadingPhoto(false);
  };

  const removePhoto = async (photoId) => {
    try {
      const { data } = await axios.delete(`${API}/listings/${listingId}/photos/${photoId}`, { withCredentials: true });
      setUploadedPhotos(data.photos || []);
    } catch (e) {
      toast.error('Failed to remove photo');
    }
  };

  const makeHero = async (photoId) => {
    const idx = uploadedPhotos.findIndex((p) => p.id === photoId);
    if (idx < 0) return;
    const reordered = [
      uploadedPhotos[idx],
      ...uploadedPhotos.slice(0, idx),
      ...uploadedPhotos.slice(idx + 1),
    ];
    setUploadedPhotos(reordered);
    setHeroPhotoId(photoId);
    try {
      await axios.patch(
        `${API}/listings/${listingId}/photos/reorder`,
        { photo_ids: reordered.map((p) => p.id) },
        { withCredentials: true }
      );
    } catch (e) {
      toast.error('Failed to update hero photo');
    }
  };

  const [dragIndex, setDragIndex] = useState(null);

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const next = [...uploadedPhotos];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setUploadedPhotos(next);
    setDragIndex(null);
    try {
      await axios.patch(
        `${API}/listings/${listingId}/photos/reorder`,
        { photo_ids: next.map((p) => p.id) },
        { withCredentials: true }
      );
    } catch (e) {
      toast.error('Failed to save photo order');
    }
  };

  const handleFinish = () => {
    if (uploadedPhotos.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }
    toast.success("Listing submitted! Our team will reach out to verify your property soon.");
    setTimeout(() => navigate('/host/dashboard'), 1500);
  };

  const toggleMonth = (monthValue) => {
    if (form.available_months.includes(monthValue)) {
      const nextPrices = { ...(form.monthly_prices || {}) };
      delete nextPrices[monthValue];
      setForm({
        ...form,
        available_months: form.available_months.filter((m) => m !== monthValue),
        monthly_prices: nextPrices,
      });
    } else {
      const nextPrices = { ...(form.monthly_prices || {}) };
      // Seed with the base price for convenience
      nextPrices[monthValue] = nextPrices[monthValue] || form.monthly_price || '';
      setForm({
        ...form,
        available_months: [...form.available_months, monthValue],
        monthly_prices: nextPrices,
      });
    }
  };

  const setMonthPrice = (monthValue, price) => {
    setForm({
      ...form,
      monthly_prices: { ...(form.monthly_prices || {}), [monthValue]: price },
    });
  };

  if (authLoading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-ink"></div>
      </div>
    );
  }

  if (!user || user.role !== 'host') {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-16 px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-4xl mb-4 text-ink">Host account required</h1>
          <Button onClick={() => navigate('/register')} className="btn-editorial btn-solid">
            Create Host Account
          </Button>
        </div>
      </div>
    );
  }

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-16 px-6">
        <div className="max-w-md text-center">
          <div className="caps text-muted mb-3">Payment required</div>
          <h1 className="font-serif text-4xl mb-4 text-ink">
            Activate your <em className="italic">listing</em>
          </h1>
          <p className="text-muted mb-8">Pay the R950 listing fee (or use a discount code) to create your first listing.</p>
          <Button onClick={() => navigate('/list-property')} className="btn-editorial btn-solid">
            Continue <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    );
  }

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-8 pb-6 border-b" style={{ borderBottomColor: 'var(--line)' }}>
          <div className="caps text-muted mb-3">Step {step} of {totalSteps} {editingId && '· Editing'}</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-2" data-testid="create-listing-title">
            {editingId ? <>Edit your <em className="italic">listing</em></> : <>Create your <em className="italic">listing</em></>}
          </h1>
          {editingId && (
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { n: 1, label: 'Basics' },
                { n: 2, label: 'Location' },
                { n: 3, label: 'Pricing' },
                { n: 4, label: 'Amenities' },
                { n: 5, label: 'Photos' },
              ].map(({ n, label }) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStep(n)}
                  className={`px-3 py-1.5 text-xs caps border transition-colors ${
                    step === n
                      ? 'bg-ink text-paper border-ink'
                      : 'border-gray-400 text-ink hover:border-ink bg-paper'
                  }`}
                  data-testid={`jump-step-${n}`}
                >
                  {n}. {label}
                </button>
              ))}
            </div>
          )}
          <div className="h-0.5 bg-ink/10 mt-6">
            <div className="h-full bg-ink transition-all duration-300" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-basic-info">
            <h2 className="font-serif text-2xl mb-4">Tell us about your property</h2>
            <div>
              <Label className="caps text-ink mb-2 block">Listing Title</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Sunny 2-bedroom apartment near Sea Point Promenade"
                className="rounded-none border-gray-400 bg-paper"
                data-testid="input-title"
              />
            </div>
            <div>
              <Label className="caps text-ink mb-2 block">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe your property, its features, and what makes it special..."
                rows={5}
                className="rounded-none border-gray-400 bg-paper"
                data-testid="input-description"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="caps text-ink mb-2 block">Property Type</Label>
                <Select value={form.property_type} onValueChange={(v) => updateField('property_type', v)}>
                  <SelectTrigger className="rounded-none border-gray-400 bg-paper" data-testid="input-property-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caps text-ink mb-2 block">Bedrooms</Label>
                <Input
                  type="number"
                  value={form.bedrooms}
                  onChange={(e) => updateField('bedrooms', e.target.value)}
                  min="0"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-bedrooms"
                />
              </div>
              <div>
                <Label className="caps text-ink mb-2 block">Bathrooms</Label>
                <Input
                  type="number"
                  value={form.bathrooms}
                  onChange={(e) => updateField('bathrooms', e.target.value)}
                  min="0"
                  step="0.5"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-bathrooms"
                />
              </div>
            </div>
            <div>
              <Label className="caps text-ink mb-2 block">What are you offering?</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updateField('place_type', 'entire_place')}
                  className={`p-4 border text-left transition-colors ${
                    form.place_type === 'entire_place'
                      ? 'bg-ink text-paper border-ink'
                      : 'border-gray-400 hover:border-ink text-ink bg-paper'
                  }`}
                  data-testid="place-type-entire"
                >
                  <div className="font-serif text-lg mb-1">Entire place</div>
                  <div className={`text-xs ${form.place_type === 'entire_place' ? 'text-paper/70' : 'text-muted'}`}>Guests have the whole property to themselves</div>
                </button>
                <button
                  type="button"
                  onClick={() => updateField('place_type', 'private_room')}
                  className={`p-4 border text-left transition-colors ${
                    form.place_type === 'private_room'
                      ? 'bg-ink text-paper border-ink'
                      : 'border-gray-400 hover:border-ink text-ink bg-paper'
                  }`}
                  data-testid="place-type-room"
                >
                  <div className="font-serif text-lg mb-1">Private room</div>
                  <div className={`text-xs ${form.place_type === 'private_room' ? 'text-paper/70' : 'text-muted'}`}>Guests have their own room; other spaces shared</div>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="caps text-ink mb-2 block">Size (m²)</Label>
                <Input
                  type="number"
                  value={form.square_metres}
                  onChange={(e) => updateField('square_metres', e.target.value)}
                  placeholder="85"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-square-metres"
                />
              </div>
              <div>
                <Label className="caps text-ink mb-2 block">WiFi Speed (Mbps)</Label>
                <Input
                  type="number"
                  value={form.wifi_speed}
                  onChange={(e) => updateField('wifi_speed', e.target.value)}
                  placeholder="100"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-wifi-speed"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-location">
            <h2 className="font-serif text-2xl mb-4">Where&apos;s it located?</h2>
            <div>
              <Label className="caps text-ink mb-2 block">Neighbourhood</Label>
              <Select value={form.neighbourhood} onValueChange={(v) => updateField('neighbourhood', v)}>
                <SelectTrigger className="rounded-none border-gray-400 bg-paper" data-testid="input-neighbourhood">
                  <SelectValue placeholder="Select a neighbourhood" />
                </SelectTrigger>
                <SelectContent>
                  {NEIGHBOURHOODS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caps text-ink mb-2 block">Full street address <span className="text-muted normal-case tracking-normal">(kept private)</span></Label>
              <AddressAutocomplete
                value={form.approximate_address}
                onChange={(v) => updateField('approximate_address', v)}
                onSelect={({ formatted_address, lat, lng }) => {
                  setForm((f) => ({
                    ...f,
                    approximate_address: formatted_address,
                    lat: lat ?? f.lat,
                    lng: lng ?? f.lng,
                  }));
                }}
                placeholder="e.g. 86 Tripp Bay Road, Green Point"
                testid="input-address"
              />
              <p className="text-xs text-muted mt-1 font-serif italic">
                Start typing and pick the matching Cape Town address from the dropdown. Guests only ever see the suburb name and an approximate pin on the map — the full street address stays private until you share it with them directly.
              </p>
              {form.lat && form.lng && (
                <p className="text-xs text-muted mt-1 caps">
                  Pinned location captured ✓
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Pricing & Availability */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-pricing">
            <h2 className="font-serif text-2xl mb-4">Pricing & availability</h2>
            <div>
              <Label className="caps text-ink mb-2 block">Base monthly price (ZAR)</Label>
              <Input
                type="number"
                value={form.monthly_price}
                onChange={(e) => updateField('monthly_price', e.target.value)}
                placeholder="25000"
                className="rounded-none border-gray-400 bg-paper text-xl font-serif"
                data-testid="input-price"
              />
              <p className="text-xs text-muted mt-1 font-serif italic">Used as the fallback and to seed each month&apos;s price. You can adjust individual months below.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="caps text-ink mb-2 block">Minimum stay (nights)</Label>
                <Input
                  type="number"
                  value={form.minimum_stay}
                  onChange={(e) => updateField('minimum_stay', e.target.value)}
                  min="28"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-min-stay"
                />
                <p className="text-xs text-muted mt-1 font-serif italic">28 nights minimum enforced.</p>
              </div>
              <div>
                <Label className="caps text-ink mb-2 block">Maximum stay (nights, optional)</Label>
                <Input
                  type="number"
                  value={form.maximum_stay}
                  onChange={(e) => updateField('maximum_stay', e.target.value)}
                  placeholder="180"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="input-max-stay"
                />
              </div>
            </div>
            <div>
              <Label className="caps text-ink mb-3 block">Available months (tap to toggle)</Label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                {MONTHS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => toggleMonth(m.value)}
                    className={`p-3 text-xs border transition-colors ${
                      form.available_months.includes(m.value)
                        ? 'bg-ink text-paper border-ink'
                        : 'border-gray-400 hover:border-ink text-ink bg-paper'
                    }`}
                    data-testid={`month-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {form.available_months.length > 0 && (
                <div className="border border-gray-300 p-4 bg-paper">
                  <div className="caps text-ink text-xs mb-3">Price per available month (ZAR)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {form.available_months.map((mv) => {
                      const label = new Date(mv + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' });
                      return (
                        <div key={mv} className="flex items-center gap-3">
                          <div className="w-40 text-sm text-ink">{label}</div>
                          <div className="flex-1 flex items-center gap-2">
                            <span className="font-serif text-muted">R</span>
                            <Input
                              type="number"
                              value={form.monthly_prices?.[mv] ?? ''}
                              onChange={(e) => setMonthPrice(mv, e.target.value)}
                              placeholder={form.monthly_price || '25000'}
                              className="rounded-none border-gray-400 bg-paper"
                              data-testid={`month-price-${mv}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Amenities & Contact */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-amenities">
            <h2 className="font-serif text-2xl mb-4">Amenities & contact</h2>
            {AMENITIES_GROUPS.map((group) => (
              <div key={group.title} className="pb-4 border-b" style={{ borderBottomColor: 'var(--line)' }}>
                <Label className="caps text-ink mb-3 block">{group.title}</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {group.items.map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`amenity-${key}`}
                        checked={form[key]}
                        onCheckedChange={(v) => updateField(key, v)}
                        className="rounded-none border-gray-500"
                        data-testid={`amenity-${key}`}
                      />
                      <Label htmlFor={`amenity-${key}`} className="text-sm cursor-pointer">{label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="pt-4">
              <Label className="caps text-ink mb-3 block">Contact Methods</Label>
              <p className="text-xs text-muted font-serif italic mb-4">
                Choose which contact methods are visible to guests. You can hide any of them.
              </p>
              <div className="space-y-5">
                <div>
                  <Label className="text-xs text-muted mb-1 block">WhatsApp number <span className="text-red-600">*</span> <span className="italic">(required)</span></Label>
                  <div className="flex gap-3 items-start">
                    <Input
                      value={form.contact_whatsapp}
                      onChange={(e) => updateField('contact_whatsapp', e.target.value)}
                      placeholder="+27 82 123 4567 (with country code)"
                      className="rounded-none border-gray-400 bg-paper flex-1"
                      data-testid="input-whatsapp"
                    />
                    <div className="flex items-center gap-2 pt-3">
                      <Checkbox
                        id="whatsapp-visible"
                        checked={form.contact_whatsapp_visible}
                        onCheckedChange={(v) => updateField('contact_whatsapp_visible', v)}
                        className="rounded-none"
                      />
                      <Label htmlFor="whatsapp-visible" className="text-xs cursor-pointer whitespace-nowrap">Show to guests</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted mb-1 block">Email</Label>
                  <div className="flex gap-3 items-start">
                    <Input
                      value={form.contact_email}
                      onChange={(e) => updateField('contact_email', e.target.value)}
                      placeholder="you@example.com"
                      className="rounded-none border-gray-400 bg-paper flex-1"
                      data-testid="input-contact-email"
                    />
                    <div className="flex items-center gap-2 pt-3">
                      <Checkbox
                        id="email-visible"
                        checked={form.contact_email_visible}
                        onCheckedChange={(v) => updateField('contact_email_visible', v)}
                        className="rounded-none"
                      />
                      <Label htmlFor="email-visible" className="text-xs cursor-pointer whitespace-nowrap">Show to guests</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted mb-1 block">Phone <span className="italic">(private by default)</span></Label>
                  <div className="flex gap-3 items-start">
                    <Input
                      value={form.contact_phone}
                      onChange={(e) => updateField('contact_phone', e.target.value)}
                      placeholder="+27 82 123 4567"
                      className="rounded-none border-gray-400 bg-paper flex-1"
                      data-testid="input-phone"
                    />
                    <div className="flex items-center gap-2 pt-3">
                      <Checkbox
                        id="phone-visible"
                        checked={form.contact_phone_visible}
                        onCheckedChange={(v) => updateField('contact_phone_visible', v)}
                        className="rounded-none"
                        data-testid="phone-visible-toggle"
                      />
                      <Label htmlFor="phone-visible" className="text-xs cursor-pointer whitespace-nowrap">Show to guests</Label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t" style={{ borderTopColor: 'var(--line)' }}>
              <div className="flex items-start gap-3 pt-4">
                <Checkbox
                  id="promo-consent"
                  checked={!!form.promo_consent}
                  onCheckedChange={(v) => updateField('promo_consent', !!v)}
                  className="rounded-none mt-0.5"
                  data-testid="promo-consent-checkbox"
                />
                <Label htmlFor="promo-consent" className="text-sm cursor-pointer leading-snug">
                  Allow capetownmonthly to promote my listing on social media and WhatsApp groups.
                  <span className="block text-xs text-muted mt-1">Ticked = Yes. Leave unticked for No.</span>
                </Label>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Photos */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-media">
            <h2 className="font-serif text-2xl mb-2">Photos</h2>
            <p className="text-muted mb-6 font-serif italic">Add up to 8 photos, each up to 10 MB &mdash; we&apos;ll compress them automatically. Drag to reorder &mdash; the first photo is your cover.</p>

            {/* Photos */}
            <div className="border border-gray-400 p-6 bg-paper">
              <div className="flex items-center justify-between mb-4">
                <Label className="caps text-ink flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
                  Photos ({uploadedPhotos.length}/8)
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={uploadedPhotos.length >= 8}
                    data-testid="upload-photos-input"
                  />
                  <span className={`btn-editorial btn-solid text-xs inline-flex ${uploadedPhotos.length >= 8 ? 'opacity-40 cursor-not-allowed' : ''}`} style={{ padding: '10px 20px' }}>
                    {uploadingPhoto ? 'Uploading...' : (uploadedPhotos.length >= 8 ? 'Max reached' : 'Add photos')}
                  </span>
                </label>
              </div>
              {uploadedPhotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {uploadedPhotos.map((p, i) => (
                    <div
                      key={p.id || i}
                      draggable
                      onDragStart={() => handleDragStart(i)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(i)}
                      className={`aspect-square bg-gray-200 relative border cursor-grab ${dragIndex === i ? 'opacity-40' : 'border-transparent'} ${i === 0 ? 'ring-2 ring-ink' : ''}`}
                      data-testid={`photo-${i}`}
                    >
                      <img src={p.storage_path?.startsWith('http') ? p.storage_path : `${API}/files/${p.storage_path}`} alt="" className="w-full h-full object-cover pointer-events-none" />
                      {i === 0 && (
                        <div className="absolute top-1 left-1 bg-ink text-paper text-[9px] px-2 py-0.5 caps flex items-center gap-1">
                          <Star className="w-3 h-3 fill-paper" strokeWidth={0} /> Cover
                        </div>
                      )}
                      {/* Delete X — always visible top-right */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removePhoto(p.id); }}
                        className="absolute top-1 right-1 bg-ink text-paper w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10"
                        data-testid={`remove-photo-${i}`}
                        aria-label="Remove photo"
                        title="Remove photo"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2} />
                      </button>
                      {/* Make cover — always visible bottom-strip for non-cover photos */}
                      {i !== 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); makeHero(p.id); }}
                          className="absolute bottom-0 inset-x-0 bg-ink/85 hover:bg-ink text-paper text-[9px] caps py-1.5 transition-colors"
                          data-testid={`make-hero-${i}`}
                        >
                          Make cover
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted italic font-serif">No photos yet — click &quot;Add photos&quot; to upload (max 8, up to 10 MB — we&apos;ll auto-compress)</p>
              )}
            </div>

            {/* Verification note */}
            <div className="bg-ink/5 border p-6" style={{ borderColor: 'var(--line)' }}>
              <div className="caps text-muted mb-2">Verification</div>
              <h3 className="font-serif text-lg mb-2">We&apos;ll reach out personally</h3>
              <p className="text-sm text-muted">
                Our team will contact you within 48 hours to verify your property through a video call or in-person walkthrough. Once verified, your listing gets the <strong className="text-ink">Verified</strong> badge and goes live.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-8 mt-8 border-t flex-wrap gap-3" style={{ borderTopColor: 'var(--line)' }}>
          {step > 1 && (step < 5 || editingId) ? (
            <button
              onClick={() => setStep(step - 1)}
              className="btn-editorial btn-ghost"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
            </button>
          ) : <div />}

          <div className="flex gap-3 flex-wrap ml-auto">
            {/* Save draft — visible on every step except the finished/photos step */}
            {step < 5 && (
              <button
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="btn-editorial btn-ghost"
                data-testid="btn-save-draft"
              >
                {savingDraft ? 'Saving…' : 'Save draft'}
              </button>
            )}

            {step < 4 && (
              <button
                onClick={() => { if (validateStep(step)) setStep(step + 1); }}
                className="btn-editorial btn-solid"
                data-testid="btn-next"
              >
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            {step === 4 && (
              <button
                onClick={handleSubmitListing}
                disabled={submitting}
                className="btn-editorial btn-solid"
                data-testid="btn-save-listing"
              >
                {submitting ? 'Saving...' : (editingId ? 'Save changes' : 'Save & Continue')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            {step === 5 && (
              editingId ? (
                <button
                  onClick={() => navigate('/host/dashboard')}
                  className="btn-editorial btn-solid"
                  data-testid="btn-done-editing"
                >
                  Done <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={uploadedPhotos.length === 0}
                  className={`btn-editorial btn-solid ${uploadedPhotos.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid="btn-finish"
                  title={uploadedPhotos.length === 0 ? 'Add at least one photo to submit' : ''}
                >
                  {uploadedPhotos.length === 0 ? 'Add a photo first' : 'Submit for review'} <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
